import React, { ReactElement, lazy } from 'react';
import './styles-overrides.css';
import { ParentProvider } from '@paypalcorp/pp-com-components/components/ParentProvider';
import { Placement } from '@paypalcorp/pp-com-components/components/Placement';
import { EmotionCMSPage } from '@paypalcorp/cms-engine/components';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import { NavFragment } from '@paypalcorp/globalnavsdk/NavFragment';
import { RegionalBannerFragment } from '@paypalcorp/regionalbannersdk/RegionalBannerFragment';
import { DocumentBody } from '@paypalcorp/pp-com-components/components/DocumentBody';
import type { AppProps, PreviewCustomValidationComponent } from '@paypalcorp/cms-engine';
import type { EmotionCache } from '@emotion/cache';
import { overrideEntryPathForMPP, overridePageTheme } from '../../utils/commonUtils';
import { QuantumLeapFooter } from '../components/QuantumLeapFooter';

interface CustomAppProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emotionCache: EmotionCache;
  renderContext: AppProps;
}

export default function App({
  emotionCache, renderContext,
}: Readonly<CustomAppProps>) {
  const {
    componentKit, pageContext, pageModel, ...props
  } = renderContext;

  const ComponentsMap: Record<string, ReactElement | any> = {};
  try {
    Object.keys(componentKit).forEach((key) => {
      ComponentsMap[key] = componentKit[key]?.bundle || lazy(() => import(`../../../node_modules/@paypalcorp/pp-com-components/dist/components/${key}.js`));
    });
  } catch (error) {
    console.error('Error loading components: ', error);
  }
  const customValidations: PreviewCustomValidationComponent[] = [];
  if (pageContext.additionalContext.preview?.enabled) {
    customValidations.push(lazy(() => import('../components/validations/PageServedByMPP')));
  }
  const {
    cmsEngineContext: {
      environment: {
        pageURI = '',
        isRTL = false,
        enableSectionTransitions = false,
      } = {},
    },
    additionalContext: {
      isPayPalAppWebview = false,
    } = {},
  } = pageContext;

  const isHomeBusiness = pageURI === 'home-business';
  const pageContextThemeOverride = overridePageTheme(pageContext);
  const pageContextAppEntryPointOverride = overrideEntryPathForMPP(pageContextThemeOverride);

  const bodyClasses = ['document-body'];
  if (pageModel.webViewStyleOverride && isPayPalAppWebview) {
    bodyClasses.push('ppui-5-webview');
  }
  if (pageModel.fullBleedPage || isHomeBusiness) {
    bodyClasses.push('full-width');
  }

  return (
    <EmotionCMSPage
      data-testid="cms-page"
      componentKit={{
        Parent: ParentProvider,
        Placement,
        ...ComponentsMap,
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      theme={PAYPAL_THEME}
      hostedBy="ppcmsnodeweb"
      pageContext={pageContextAppEntryPointOverride}
      pageModel={pageModel}
      {...props}
      emotionCache={emotionCache}
      previewCustomValidations={customValidations}
      render={({
        aboveHeader, body, belowFooter,
      }) => (
        <>
          <RegionalBannerFragment />
          {aboveHeader}
          <NavFragment type="header" />
          <DocumentBody
            className={bodyClasses.join(' ')}
            data-testid="document-body-element"
            disableSectionTransitions={!enableSectionTransitions}
          >
            {body}
          </DocumentBody>
          <QuantumLeapFooter belowFooter={belowFooter} />
        </>
      )}
    />
  );
}
